import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { CssBaseline, PaletteMode } from '@mui/material'
import { ThemeOptions, ThemeProvider, createTheme } from '@mui/material/styles'
import { ToastProvider } from './components/ToastProvider'

type ColorMode = PaletteMode

interface ColorModeContextValue {
  mode: ColorMode
  toggleColorMode: () => void
  setColorMode: (mode: ColorMode) => void
}

const STORAGE_KEY = 'webmix-it-theme'
const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)'

export const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined)

const getDesignTokens = (mode: ColorMode): ThemeOptions => {
  const isDark = mode === 'dark'

  return {
    palette: {
      mode,
      primary: {
        main: isDark ? '#4dabf7' : '#0052cc',
        light: isDark ? '#74c0fc' : '#4d8ae0',
        dark: isDark ? '#1c7ed6' : '#003a99',
        contrastText: '#ffffff',
      },
      secondary: {
        main: isDark ? '#00b4d8' : '#0096c7',
        light: isDark ? '#48cae4' : '#33bde5',
        dark: isDark ? '#0090ad' : '#0077a3',
        contrastText: '#ffffff',
      },
      background: {
        default: isDark ? '#121212' : '#ffffff',
        paper: isDark ? '#1e1e1e' : '#f7f9fc',
      },
      text: {
        primary: isDark ? '#e6e6e6' : '#1a1a1a',
        secondary: isDark ? '#cbd5f5' : '#334155',
      },
      divider: isDark ? '#2f2f2f' : '#e2e8f0',
    },
    shape: {
      borderRadius: 14,
    },
    typography: {
      fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif',
      h1: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#121212' : '#ffffff',
            color: isDark ? '#e6e6e6' : '#1a1a1a',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
          a: {
            color: isDark ? '#74c0fc' : '#0052cc',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 10px rgba(15, 23, 42, 0.08)',
            backdropFilter: 'blur(18px)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: '1.5rem',
            paddingBlock: '0.75rem',
            boxShadow: 'none',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            color: '#ffffff',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
            boxShadow: isDark
              ? '0 10px 35px rgba(15, 23, 42, 0.45)'
              : '0 12px 28px rgba(15, 23, 42, 0.12)',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: isDark
                ? '0 18px 40px rgba(15, 23, 42, 0.55)'
                : '0 18px 34px rgba(15, 23, 42, 0.18)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 14,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  }
}

const getInitialMode = (): ColorMode => {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const stored = localStorage.getItem(STORAGE_KEY) as ColorMode | null
  if (stored === 'light' || stored === 'dark') {
    return stored
  }

  return window.matchMedia(PREFERS_DARK_QUERY).matches ? 'dark' : 'light'
}

const applyDomSideEffects = (mode: ColorMode) => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.dataset.theme = mode
  document.body.classList.add('theme-transitioning')
  window.setTimeout(() => {
    document.body.classList.remove('theme-transitioning')
  }, 280)

  const metaTheme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (metaTheme) {
    metaTheme.content = mode === 'dark' ? '#121212' : '#ffffff'
  }
}

export const AppThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>(() => getInitialMode())

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    localStorage.setItem(STORAGE_KEY, mode)
    applyDomSideEffects(mode)
  }, [mode])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const media = window.matchMedia(PREFERS_DARK_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'light' || stored === 'dark') {
        return
      }
      setMode(event.matches ? 'dark' : 'light')
    }

    if (media.addEventListener) {
      media.addEventListener('change', handleChange)
    } else {
      media.addListener(handleChange)
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleChange)
      } else {
        media.removeListener(handleChange)
      }
    }
  }, [])

  const toggleColorMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'))
  }, [])

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode])

  const contextValue = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      toggleColorMode,
      setColorMode: setMode,
    }),
    [mode, toggleColorMode],
  )

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
        <ToastProvider />
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export const useColorMode = (): ColorModeContextValue => {
  const context = useContext(ColorModeContext)
  if (!context) {
    throw new Error('useColorMode must be used within an AppThemeProvider')
  }
  return context
}

export { getDesignTokens }
